import { useCallback, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DEFAULT_COLOR_HEX } from 'companySettings/components/PartFinderAttributeValues';
import { AttributeStatus, AttributeType } from 'companySettings/enums';
import { usePartFinderAttributeActions } from 'companySettings/hooks';
import { IPartFinderAttribute } from 'companySettings/types';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';

import { mapPartFinderAttributePayload } from './mappers';
import { upsertPartFinderAttributeSchema } from './schema';
import { IUpsertPartFinderAttributeFormData } from './types';
import { UpsertPartFinderAttributeFormFields } from './UpsertPartFinderAttributeFormFields';

interface IProps {
  attribute?: IPartFinderAttribute;
  isOpen: boolean;
  onClose: () => void;
}

export const UpsertPartFinderAttribute = ({ attribute, isOpen, onClose }: IProps) => {
  const [isSaved, setIsSaved] = useState(false);

  const { clearNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { savePartFinderAttribute } = usePartFinderAttributeActions();

  const defaultValues = useMemo(
    (): IUpsertPartFinderAttributeFormData => ({
      name: attribute?.name ?? '',
      newColorHex: DEFAULT_COLOR_HEX,
      newColorName: '',
      newRangeIncrement: '',
      newRangeMax: '',
      newRangeMin: '',
      newRangeUnit: '',
      newValue: '',
      type: attribute?.type ?? AttributeType.VALUES,
      values: attribute?.values ?? [],
    }),
    [attribute]
  );

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertPartFinderAttributeFormData>) => {
      setIsSaved(false);
      clearNotification();
      reset?.(defaultValues);
      onClose();
    },
    [clearNotification, defaultValues, onClose]
  );

  const onSubmit = useCallback(
    (status: AttributeStatus) =>
      (reset: UseFormReset<IUpsertPartFinderAttributeFormData>) =>
      async (data: IUpsertPartFinderAttributeFormData) => {
        const { success, partFinderAttribute: savedAttribute } = await savePartFinderAttribute(
          mapPartFinderAttributePayload(data, status, attribute)
        );

        if (!success) {
          return;
        }

        // The form still holds the revision it was loaded with, so a second save before the drawer
        // closes would be refused as stale.
        setIsSaved(true);

        // The row changes only once the drawer goes: the drawer can sit in the row's own cell, the
        // selection bar or the empty state, and the update re-renders or removes each of them.
        setTimeout(() => {
          handleOnClose(reset);
          updateRows(attribute ? { update: [savedAttribute] } : { add: [savedAttribute] }, {
            refreshCells: true,
          });
        }, 1000);
      },
    [attribute, handleOnClose, savePartFinderAttribute, updateRows]
  );

  return (
    <FormProvider
      schema={upsertPartFinderAttributeSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPartFinderAttributeFormFields
        attribute={attribute}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        isSaved={isSaved}
      />
    </FormProvider>
  );
};
